import { useQuery } from '@tanstack/react-query';
import { Card, Center, Icon, Spinner } from '@barev/ui';
import { navigate, useI18n, useTRPC } from '@barev/web';
import { itemName, LevelBadge, type Level } from '../lib.tsx';
import { MoveForm } from './MoveForm.tsx';

const DAY = 24 * 3600_000;

/**
 * Главная для тех, кто переезжает: план по шагам со статусами от оператора.
 * Плана ещё нет — конструктор пакета (тот же, что в квизе на сайте).
 */
export function Move() {
  const { t, dt, locale } = useI18n();
  const trpc = useTRPC();
  const me = useQuery(trpc.client.me.queryOptions());
  const plan = useQuery({ ...trpc.client.relocation.get.queryOptions(), refetchInterval: 30_000 });

  if (!plan.data || !me.data)
    return (
      <Center>
        <Spinner />
      </Center>
    );
  const lead = plan.data.lead;
  if (!lead || !lead.package) return <MoveForm />;

  const steps = lead.steps.filter((s) => s.status !== 'SKIPPED');
  const done = steps.filter((s) => s.status === 'DONE').length;
  const docsDone = lead.docs.filter((d) => d.checked).length;
  const days = lead.moveDate ? Math.ceil((new Date(lead.moveDate).getTime() - Date.now()) / DAY) : null;

  return (
    <main className="app app--hero">
      <section className="hero move-hero">
        <div className="hero__word" aria-hidden="true">
          Barev
        </div>
        <div className="bc-between" style={{ alignItems: 'flex-start', position: 'relative' }}>
          <h1 className="hero__hello">
            <small>{t('client.home.hello')}</small>
            <span>{me.data.firstName ?? t('client.home.guest')}</span>
          </h1>
          <LevelBadge level={me.data.membership.level as Level} />
        </div>
        <div className="move-hero__label">{t('client.move.title')}</div>
        <div className="move-hero__when">
          {lead.moveWhen === 'date' && lead.moveDate
            ? dt(lead.moveDate, { day: 'numeric', month: 'long', timeZone: 'UTC' })
            : t(`landing.quiz.when_${lead.moveWhen ?? 'later'}`)}
        </div>
        {days !== null && days > 0 && <div className="move-hero__days">{t('client.move.daysLeft', { count: days })}</div>}
        <div className="move-hero__bar" role="progressbar" aria-valuemin={0} aria-valuemax={steps.length} aria-valuenow={done} aria-label={t('client.move.progress', { done, total: steps.length })}>
          <span style={{ width: `${steps.length ? (done / steps.length) * 100 : 0}%` }} />
        </div>
        <div className="move-hero__progress">{t('client.move.progress', { done, total: steps.length })}</div>
      </section>

      <Card style={{ padding: '18px 18px 8px' }}>
        <h2 className="panel__title">
          {t(`landing.quiz.package_${lead.package}`)}
        </h2>
        {steps.length === 0 ? (
          <p className="bc-muted" style={{ margin: '0 0 12px' }}>
            {t('client.move.noSteps')}
          </p>
        ) : (
          <ol className="move-steps">
            {steps.map((s) => (
              <li key={s.id} className="move-step" data-status={s.status}>
                <span className="move-step__dot" aria-hidden="true">
                  {s.status === 'DONE' && <Icon name="check" size={13} strokeWidth={3} />}
                </span>
                <div className="move-step__body">
                  <div className="move-step__title">{itemName(plan.data.names, 'STEP', s, locale)}</div>
                  {s.note && <div className="move-step__note">{s.note}</div>}
                </div>
                <span className="move-step__status">{t(`client.move.status.${s.status}`)}</span>
              </li>
            ))}
          </ol>
        )}
      </Card>

      <div className="move-actions">
        <a href="#/docs" className="move-action bc-tone-docs">
          <Icon name="docs" size={24} />
          <b>{t('client.docs.title')}</b>
          <span className="bc-small">{t('client.docs.progress', { done: docsDone, total: lead.docs.length })}</span>
        </a>
        <button className="move-action bc-tone-learn" onClick={() => navigate('/chat')}>
          <Icon name="chat" size={24} />
          <b>{t('client.concierge.title')}</b>
          <span className="bc-small">{t('client.concierge.short')}</span>
        </button>
      </div>

      <Card tight className="bc-stack" style={{ gap: 6 }}>
        <div className="bc-small" style={{ fontWeight: 600 }}>
          {t('client.move.afterTitle')}
        </div>
        <div className="bc-small bc-muted">{t('client.move.afterText')}</div>
      </Card>
    </main>
  );
}
